import { Card } from '@/shared/components/ui/Card';
import { useState } from 'react';

/**
 * Regional Manager Routine Component
 * Provides guidance on how regional managers should use the Reports system
 */
export function RegionalManagerRoutine() {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Card className="p-6 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold text-purple-900">
            Regional Manager Routine
          </h2>
          <p className="text-sm text-purple-700 mt-1">
            What to check daily, weekly, and monthly
          </p>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors text-sm font-medium"
        >
          {isExpanded ? 'Hide Guide' : 'Show Guide'}
        </button>
      </div>

      {isExpanded && (
        <div className="space-y-6 mt-6">
          {/* Daily Check */}
          <section>
            <h3 className="text-lg font-semibold text-purple-900 mb-3 flex items-center">
              <span className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-2 text-sm">
                1
              </span>
              Daily Check (Quick 1–2 Minutes)
            </h3>
            <div className="ml-10 space-y-3">
              <div className="bg-white p-4 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-gray-900 mb-2">
                  1. Aging & Exceptions Report
                </h4>
                <p className="text-sm text-gray-700 mb-2">Look for:</p>
                <ul className="text-sm text-gray-700 list-disc ml-5 space-y-1">
                  <li>ORDERED frames older than 7 days (yellow)</li>
                  <li>ORDERED frames older than 14 days (red)</li>
                  <li>PENDING frames older than expected (not yet ordered)</li>
                </ul>
                <div className="mt-3 p-3 bg-blue-50 rounded border-l-4 border-blue-500">
                  <p className="text-sm text-blue-900 italic">
                    <strong>If speaking with the location:</strong> "I see several frames
                    ordered more than 14 days ago. Can you walk me through what might be
                    holding those up?"
                  </p>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-gray-900 mb-2">
                  2. Usage Indicator (Today vs Yesterday)
                </h4>
                <p className="text-sm text-gray-700 mb-2">
                  Confirm each location logged frame activity today or yesterday.
                </p>
                <div className="mt-3 p-3 bg-blue-50 rounded border-l-4 border-blue-500">
                  <p className="text-sm text-blue-900 italic">
                    <strong>If no activity on a working day:</strong> "Did anyone add
                    frames to reorder yesterday? Is there an operational reason for the
                    slowdown?"
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Weekly Check */}
          <section>
            <h3 className="text-lg font-semibold text-purple-900 mb-3 flex items-center">
              <span className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-2 text-sm">
                2
              </span>
              Weekly Check (Stronger Coaching Moment)
            </h3>
            <div className="ml-10 space-y-3">
              <div className="bg-white p-4 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-gray-900 mb-2">
                  1. Location Summary – Last 7 Days (Side-by-Side)
                </h4>
                <p className="text-sm text-gray-700 mb-2">Review:</p>
                <ul className="text-sm text-gray-700 list-disc ml-5 space-y-1">
                  <li>Frames added</li>
                  <li>Frames ordered</li>
                  <li>Frames received</li>
                  <li>Usage % (working-day calculation only)</li>
                </ul>
                <div className="mt-3 p-3 bg-blue-50 rounded border-l-4 border-blue-500">
                  <p className="text-sm text-blue-900 italic">
                    <strong>Coaching prompt:</strong> "I noticed Location A is at 85%
                    usage, but Location B is at 40%. Is there anything you need to stay
                    current?"
                  </p>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-gray-900 mb-2">
                  2. Manufacturer Performance
                </h4>
                <p className="text-sm text-gray-700 mb-2">Check:</p>
                <ul className="text-sm text-gray-700 list-disc ml-5 space-y-1">
                  <li>Which manufacturers are being ordered the most?</li>
                  <li>Are minimums being hit consistently?</li>
                  <li>Are shipments arriving on time?</li>
                </ul>
                <div className="mt-3 p-3 bg-blue-50 rounded border-l-4 border-blue-500">
                  <p className="text-sm text-blue-900 italic">
                    <strong>Coaching prompt:</strong> "You're consistently below minimum
                    order qty for Marchon. Are you entering frames every day so orders
                    batch properly?"
                  </p>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-gray-900 mb-2">
                  3. Review Weekly Ordering Efficiency
                </h4>
                <p className="text-sm text-gray-700 mb-2">
                  Check whether orders are being grouped by manufacturer properly.
                  Identify locations frequently missing minimums or free shipping.
                </p>
                <div className="mt-3 p-3 bg-blue-50 rounded border-l-4 border-blue-500">
                  <p className="text-sm text-blue-900 italic">
                    <strong>Coaching prompt:</strong> "Is the team entering reorders
                    daily so you can place efficient weekly orders?"
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Monthly Check */}
          <section>
            <h3 className="text-lg font-semibold text-purple-900 mb-3 flex items-center">
              <span className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-2 text-sm">
                3
              </span>
              Monthly Check (Bigger Pattern Analysis)
            </h3>
            <div className="ml-10 space-y-3">
              <div className="bg-white p-4 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-gray-900 mb-2">
                  1. Usage % Trend (Last 30–90 Days)
                </h4>
                <ul className="text-sm text-gray-700 list-disc ml-5 space-y-1">
                  <li>Look for steady improvement or decline</li>
                  <li>Identify locations with chronic low usage</li>
                </ul>
              </div>

              <div className="bg-white p-4 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-gray-900 mb-2">
                  2. Exception Trend (Aging Over Time)
                </h4>
                <ul className="text-sm text-gray-700 list-disc ml-5 space-y-1">
                  <li>Are old ORDERED frames piling up over the month?</li>
                  <li>Are PENDING frames staying too long?</li>
                </ul>
              </div>

              <div className="bg-white p-4 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-gray-900 mb-2">
                  3. Manufacturer Volume Overview
                </h4>
                <ul className="text-sm text-gray-700 list-disc ml-5 space-y-1">
                  <li>Identify which manufacturers are trending upward or downward</li>
                  <li>Spot unusual drops in ordering (possible operational issues)</li>
                </ul>
              </div>

              <div className="bg-white p-4 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-gray-900 mb-2">
                  4. Team Coaching Opportunity
                </h4>
                <ul className="text-sm text-gray-700 list-disc ml-5 space-y-1">
                  <li>Use data to guide scheduled 1:1s with office managers</li>
                  <li>Prepare questions based on what the reports show</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Summary Box */}
          <div className="mt-6 p-4 bg-purple-100 border-2 border-purple-300 rounded-lg">
            <h4 className="font-semibold text-purple-900 mb-2">Key Takeaway</h4>
            <p className="text-sm text-purple-900">
              Use these reports to have <strong>data-driven conversations</strong>, not just
              to spot problems. The goal is to coach locations toward consistent system
              adoption and operational excellence.
            </p>
          </div>
        </div>
      )}
    </Card>
  );
}
